import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useLang } from "../../lib/i18n";
import { useCart } from "../../lib/cart";
import { Menu, X, ArrowUpRight, ShoppingBag } from "lucide-react";

const links = (t, lang) => [
  { to: "/", label: t.nav.home, end: true },
  { to: "/services", label: t.nav.services },
  { to: "/about", label: t.nav.about },
  { to: "/trainings", label: lang === "en" ? "Trainings" : "Eğitimler" },
  { to: "/events", label: t.nav.events },
  { to: "/blog", label: t.nav.blog },
  { to: "/contact", label: t.nav.contact },
];

export default function Header() {
  const { lang, toggle, t } = useLang();
  const { count } = useCart();
  const navigationLinks = links(t, lang).map((link) => (
    link.to === "/trainings"
      ? { ...link, label: lang === "en" ? "Training & Consulting" : "Eğitim & Danışmanlık" }
      : link
  ));
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-cream-100/80 border-b border-cream-300/70"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-1 group">
          <span className="font-serif text-3xl md:text-[2rem] leading-none text-ink tracking-tight">
            in
          </span>
          <span className="font-serif italic text-3xl md:text-[2rem] leading-none text-terracotta-400 tracking-tight">
            Touch
          </span>
          <span className="ml-1 w-1.5 h-1.5 rounded-full bg-sage-400 group-hover:bg-terracotta-400 transition-colors" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navigationLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative text-[0.92rem] font-light tracking-wide transition-colors ${
                  isActive ? "text-ink" : "text-ink/65 hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-terracotta-400 transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/cart" aria-label="Sepet" className="relative p-2 text-ink/70 hover:text-ink transition-colors">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-terracotta-400 text-white text-[10px] leading-4 text-center">{count}</span>}
          </Link>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 text-[0.78rem] uppercase tracking-[0.22em] text-ink/70 hover:text-ink transition-colors px-2 py-1.5 border border-ink/15 rounded-full"
            aria-label="Toggle language"
          >
            <span className={lang === "tr" ? "text-ink font-medium" : ""}>TR</span>
            <span className="opacity-40">/</span>
            <span className={lang === "en" ? "text-ink font-medium" : ""}>EN</span>
          </button>

          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-ink text-cream-100 px-5 py-2.5 text-sm font-light hover:bg-sage-700 transition-all hover:-translate-y-0.5"
          >
            {t.nav.cta}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>

          <button
            data-testid="menu-toggle"
            onClick={() => setOpen((p) => !p)}
            className="lg:hidden p-2 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container-x bg-cream-100 border-t border-cream-300/70 pb-8 pt-4">
          <ul className="flex flex-col gap-1">
            {navigationLinks.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `block py-3 text-xl font-serif tracking-tight ${
                      isActive ? "text-terracotta-400" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3 mt-6">
            <button
              data-testid="lang-toggle-mobile"
              onClick={toggle}
              className="text-xs uppercase tracking-[0.22em] text-ink/70 px-3 py-2 border border-ink/15 rounded-full"
            >
              {lang === "tr" ? "EN" : "TR"}
            </button>
            <Link
              to="/contact"
              data-testid="mobile-header-cta"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-cream-100 px-5 py-2.5 text-sm"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
